import { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import {
  getFranchiseFlowSessionRemainingMs,
  hasFranchiseFlowAuth,
  logoutFranchiseFlow,
  persistFranchiseFlowLastPath,
  readFranchiseFlowAuth,
  validateFranchiseFlowAuth,
} from './franchiseFlowAuth';
import { prefetchDeviceLocation } from './deviceLocationCache';
import { startFranchiseGeofenceMonitor } from './franchiseGeofence';
import FranchiseFlowSessionBar from './FranchiseFlowSessionBar';
import { FranchiseFlowAuthProvider } from './FranchiseFlowAuthContext';
import { isGuestKioskPath } from './kioskLoginRedirect';

/**
 * Redirects to franchise-flow login when the device session is missing or expired (12h),
 * or when continuous geofence checks find the device outside the store radius.
 * Shows authenticated Sign out for Face Identity / Salon flows.
 * @param {{ children: import('react').ReactNode, loginPath: string }} props
 */
export default function RequireFranchiseFlowAuth({ children, loginPath }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const loggingOutRef = useRef(false);
  const auth = readFranchiseFlowAuth();
  /* Hub + analysis pages use SalonHeaderActions (inline logout). Keep floating bar
   * for desk/lobby pages that only have FaceIdentityFlowNav (back/home). */
  const path = location.pathname || '';
  const hideFloatingLogout =
    path === '/naturals/presentation' ||
    path.startsWith('/naturals/presentation/') ||
    path === '/naturals/salon/hub' ||
    path === '/naturals/salon/test' ||
    path.endsWith('/hub') ||
    path.includes('/naturals/salon/capture') ||
    path.includes('/naturals/salon/user-info') ||
    path.includes('/naturals/salon/skin') ||
    path.includes('/naturals/salon/ai-') ||
    path.includes('/naturals/salon/results') ||
    path.includes('/naturals/salon/routine') ||
    path.includes('/naturals/salon/recommendations') ||
    path.includes('/naturals/salon/treatment') ||
    path.includes('/naturals/salon/macro') ||
    path.includes('/naturals/hair') ||
    path.includes('/hair-analyzer') ||
    path.includes('/hair-insight');

  const forceLogout = async (navState = {}) => {
    if (loggingOutRef.current) return;
    loggingOutRef.current = true;
    try {
      await logoutFranchiseFlow();
    } finally {
      navigate(loginPath, { replace: true, state: navState });
      loggingOutRef.current = false;
    }
  };

  useEffect(() => {
    if (!hasFranchiseFlowAuth()) return undefined;
    const flushLastPath = () => {
      const returnTo = `${location.pathname}${location.search || ''}`;
      if (!isGuestKioskPath(returnTo) && !returnTo.includes('/login')) {
        persistFranchiseFlowLastPath(returnTo);
      }
    };
    flushLastPath();
    const onHide = () => flushLastPath();
    window.addEventListener('pagehide', onHide);
    document.addEventListener('visibilitychange', onHide);
    return () => {
      window.removeEventListener('pagehide', onHide);
      document.removeEventListener('visibilitychange', onHide);
    };
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!hasFranchiseFlowAuth()) return undefined;
    void validateFranchiseFlowAuth();
    prefetchDeviceLocation();
    return undefined;
  }, [auth?.organization_id, auth?.signed_in_at]);

  useEffect(() => {
    if (!hasFranchiseFlowAuth()) return undefined;
    const remaining = getFranchiseFlowSessionRemainingMs(auth);
    if (remaining <= 0) {
      void forceLogout({ sessionExpired: true });
      return undefined;
    }
    const timer = window.setTimeout(() => {
      void forceLogout({ sessionExpired: true });
    }, remaining);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.organization_id, auth?.signed_in_at, loginPath, navigate]);

  useEffect(() => {
    if (!hasFranchiseFlowAuth()) return undefined;
    if (auth?.geofence_enabled === false) return undefined;

    const stop = startFranchiseGeofenceMonitor(
      () => readFranchiseFlowAuth(),
      (violation) => {
        void forceLogout({
          geofenceLogout: true,
          geofenceCode: violation?.code || 'OUTSIDE_GEOFENCE',
          geofenceMessage: violation?.message || '',
        });
      },
    );

    return () => stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    auth?.organization_id,
    auth?.latitude,
    auth?.longitude,
    auth?.geofence_enabled,
    loginPath,
    navigate,
  ]);

  if (!hasFranchiseFlowAuth()) {
    const returnTo = `${location.pathname}${location.search || ''}`;
    if (!isGuestKioskPath(returnTo) && !returnTo.includes('/login')) {
      persistFranchiseFlowLastPath(returnTo);
    }
    const from = isGuestKioskPath(returnTo) ? undefined : returnTo;
    return (
      <Navigate
        to={loginPath}
        replace
        state={from ? { from } : {}}
      />
    );
  }

  const handleLogout = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await logoutFranchiseFlow();
    } finally {
      navigate(loginPath, { replace: true, state: { kioskLogout: true } });
      setBusy(false);
    }
  };

  return (
    <FranchiseFlowAuthProvider value={{ onLogout: handleLogout, logoutBusy: busy }}>
      {hideFloatingLogout ? null : (
        <FranchiseFlowSessionBar busy={busy} onLogout={handleLogout} />
      )}
      {children}
    </FranchiseFlowAuthProvider>
  );
}
